import React, { Fragment, useEffect, useState } from 'react';
import { RiSearch2Line } from 'react-icons/ri';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import CommonTable from 'components/CommonTable';
import { CustomButton, CustomInput } from 'components/CustomForm';
import useModal from 'hooks/useModal';
import AddUserModal from './AddUserModal';
import { getUsers, deleteUser } from 'network/apis/UserGroupsApi';
import { Spinner } from 'react-bootstrap';
import { TbEditCircle } from 'react-icons/tb';
import { TiDocumentDelete, TiTickOutline, TiCancelOutline } from 'react-icons/ti';
import { getUserTypeLabel } from 'helpers/strings';
import CommonPrompt from "components/DeletePrompt";
import { useLocation, useSearchParams } from 'react-router-dom';
import LogsModal from './LogsModal';
import ChangePasswordModal from './ChangePasswordModal';

const cols = [
	'حذف',
	'ویرایش',
	'رمز ورود',
	'تاریخچه فعالیت ها',
	'فعال',
	'نام کاربری',
	'نام و نام خانوادگی کاربر',
	'نوع کاربری',
	'ردیف'
];

const Holder = styled.div`
	background-color: white;
	border-bottom-left-radius: 26px;
	border-bottom-right-radius: 26px;
	width: 100%;
	margin: 0;
	padding: 0 2rem;

	div.loading {
		min-height: 30rem;
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		color: #3876e5;
	}

	div.header-card {
		display: flex;
		justify-content: space-between;
		align-items: center
		width: 100%;
		margin-bottom: 1.5rem;

		button {
			width: 10rem;
		}
	}
`;

const UsersTab = ({ currentTab = 'users' }) => {
	const [pageOffset, setPageOffset] = useState(1);
	const [searchPhrase, setSearchPhrase] = useState('');

	const {
		isOpen,
		handleOpenModal,
		handleCloseModal,
		data: editData,
	} = useModal();

	const {
		isOpen: isDeleteModalOpen,
		handleOpenModal: handleOpenDeleteModal,
		handleCloseModal: handleCloseDeleteModel,
		data: deleteModalData,
	} = useModal();

	const {
		isOpen: isLogsModalOpen,
		handleOpenModal: handleOpenLogsModal,
		handleCloseModal: handleCloseLogsModel,
		data: logsModalData,
	} = useModal();

	const {
		isOpen: isPasswordModalOpen,
		handleOpenModal: handleOpenPasswordModal,
		handleCloseModal: handleClosePasswordModel,
		data: passwordModalData,
	} = useModal();

	const {
		data: usersData,
		refetch,
		isLoading,
	} = useQuery(["admin-get-users"], () => getUsers(pageOffset), {
		refetchOnWindowFocus: false,
		enabled: currentTab === 'users'
	});

	useEffect(() => {		
		refetch();
	}, [pageOffset]);

	const deleteMutation = useMutation(deleteUser, {
		onSuccess: (res) => {
			if (res && res.status === 204) {
				toast(`با موفقیت حذف گردید`, {
					type: "success",
					position: "bottom-right",
				});
				refetch();
			}
		},
		onError: () => {
			toast(`خطا در حذف، لطفا اتصال خود را بررسی کنید`, {
				type: "error",
				position: "bottom-right",
			});
		},
	});

	const getList = () => {
		const shouldSearch = searchPhrase.trim().length > 0;

		if (shouldSearch) {
			return usersData?.data?.items?.filter((item) => item?.full_name.includes(searchPhrase));
		}

		return usersData?.data?.items ?? [];
	};

	const getItemNumber = (index) => {
		if (pageOffset > 1) {
			return (index + 1) + 20;
		}

		else return (index + 1);
	};

	return (
		<Holder>
			{isLoading ? (
				<div className="loading">
					<Spinner animation="border" role="status" />
				</div>
			) : (
				<Fragment>
					<div className="header-card">
						<div className='d-flex align-items-center w-100'>
							<RiSearch2Line size='22px' color='black' />
							<CustomInput
								className="mx-3 w-25"
								placeholder='جستجو نام کاربر'
								radius="14"
								onChange={(e) => setSearchPhrase(e.target.value)}
							/>
						</div>
						<CustomButton onClick={() => handleOpenModal(null)}>افزودن کاربر</CustomButton>
					</div>
					<CommonTable
						cols={cols}
						pageCount={parseInt(usersData?.data?.num_pages ?? 1)}
						pageOffset={pageOffset - 1}
						fetchPageAt={(newOffset) => {
							setPageOffset(newOffset);
						}}
					>
						{getList().map((item, index) => (
							<tr key={item?.user_id}>
								<td>
									<TiDocumentDelete
										onClick={() => {
											handleOpenDeleteModal(item);
										}}
									/>
								</td>
								<td>
									<TbEditCircle
										onClick={() => {
											handleOpenModal(item);
										}}
									/>
								</td>
								<td>
									<CustomButton
										onClick={() => handleOpenPasswordModal(item)}
										color='#0278EE'
										fontSize={10}
									>
										تغییر
									</CustomButton>
								</td>
								<td>
									<CustomButton
										onClick={() => handleOpenLogsModal(item)}
										color='#0278EE'
										fontSize={10}
									>
										نمایش
									</CustomButton>
								</td>
								<td>{item?.is_active ? <TiTickOutline size={19} /> : <TiCancelOutline size={19} />}</td>
								<td>{item?.username}</td>
								<td>{item?.full_name}</td>
								<td>{getUserTypeLabel(item?.user_type ?? -1)}</td>
								<td>{getItemNumber(index)}</td>
							</tr>
						))}
					</CommonTable>
				</Fragment>
			)}
			{isOpen && (
				<AddUserModal
					isOpen
					onClose={handleCloseModal}
					data={editData}
					onCreateSuccess={refetch}
				/>
			)}
			{isDeleteModalOpen && (
				<CommonPrompt
					isOpen={isDeleteModalOpen}
					onClose={handleCloseDeleteModel}
					onActionTap={() => {
						handleCloseDeleteModel();
						deleteMutation.mutate({ id: deleteModalData?.user_id });
					}}
				/>
			)}
			{isLogsModalOpen && (
				<LogsModal
					isOpen
					onClose={handleCloseLogsModel}
					data={logsModalData}
					referrer='admin'
				/>
			)}
			{isPasswordModalOpen && (
				<ChangePasswordModal
					isOpen
					onClose={handleClosePasswordModel}
					data={passwordModalData}
				/>
			)}
		</Holder>
	);
};

export default UsersTab;
